import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, effect, untracked, input, model, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { WeatherService, WeatherData } from '../../../../services/weather.service';
import { LineItemModel } from '../../../../models/template.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-weather-control',
  standalone: true,
  imports: [CommonModule, TableModule],
  template: `
    <div class="checklist-control-container" [class.checklist-control-container--error]="hasValidationError()">
      <label class="checklist-control-label">
        {{ lineItem().name }}
        @if (lineItem().isRequired) {
          <span class="required-indicator">*</span>
        }
      </label>

      <div class="weather-table-container">
        @if (loading) {
          <div class="loading-message">Loading weather data...</div>
        }
        @if (error) {
          <div class="error-message">{{ error }}</div>
        }
        @if (!loading && !error && weatherData().length > 0) {
          <p-table [value]="weatherData()" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
              <tr>
                <th>Time</th>
                <th>Condition</th>
                <th>Temperature</th>
                <th>Precipitation</th>
                <th>Humidity</th>
                <th>Wind</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-data>
              <tr>
                <td>{{ data.time }}</td>
                <td>
                  <i [class]="'pi ' + data.icon" style="margin-right: 8px;"></i>
                  {{ data.condition }}
                </td>
                <td>{{ data.temperature }}</td>
                <td>{{ data.precipitation }}</td>
                <td>{{ data.humidity }}</td>
                <td>{{ data.wind }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </div>
      @if (hasValidationError()) {
        <small class="p-error d-block">This field is required</small>
      }
    </div>
  `,
  styles: [`
    .required-indicator {
      color: var(--color-error-mid);
      font-weight: bold;
      margin-left: 2px;
    }
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.75rem;
      border: 1px solid var(--surface-border);
      border-radius: 6px;
      background: var(--surface-0);
    }
    .checklist-control-container--error {
      border-color: #ef4444;
      background: #fef2f2;
    }
    .checklist-control-label {
      font-weight: 500;
    }
    .weather-table-container {
      margin-top: 0.5rem;
    }
    .loading-message {
      padding: 1rem;
      text-align: center;
      color: var(--text-color-secondary);
    }
    .error-message {
      padding: 1rem;
      text-align: center;
      color: #ef4444;
    }
    ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background-color: var(--surface-100);
      font-weight: 600;
    }
    ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      padding: 0.75rem;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WeatherControlComponent implements OnInit, OnDestroy {
  lineItem = input.required<LineItemModel>();
  value = model<WeatherData[]>([]);
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  // Input signals for location and date
  city = input<string>('');
  state = input<string>('');
  zip = input<string>('');
  latitude = input<string>('');
  longitude = input<string>('');
  date = input<Date | undefined>(undefined);

  // Internal state
  weatherData = model<WeatherData[]>([]);
  loading = false;
  error = '';

  private destroy$ = new Subject<void>();
  private lastFetchedDate: string = '';
  private isInitialLoad: boolean = true;
  hasValidationError = computed(() =>
    this.showError() && this.lineItem().isRequired && this.weatherData().length === 0
  );

  constructor(
    private weatherService: WeatherService,
    private cdr: ChangeDetectorRef
  ) {
    // Watch for date changes and fetch weather data
    effect(() => {
      const currentDate = this.date();
      const city = this.city();
      const state = this.state();
      const zip = this.zip();
      const latitude = this.latitude();
      const longitude = this.longitude();
      const savedData = untracked(() => this.value());

      // On initial load, check if we have saved data
      if (this.isInitialLoad && savedData && savedData.length > 0) {
        // Load from saved data on first load
        this.weatherData.set(savedData);
        this.loading = false;
        this.error = '';
        this.isInitialLoad = false;
        this.cdr.markForCheck();
        return;
      }

      // After initial load or if no saved data, fetch from API when date changes
      if (currentDate && this.hasLocationData(latitude, longitude, zip, city, state)) {
        const dateStr = this.formatDate(currentDate);

        // Only fetch if date changed
        if (dateStr !== this.lastFetchedDate) {
          this.lastFetchedDate = dateStr;
          this.isInitialLoad = false;
          this.fetchWeatherData({
            city,
            state,
            zip,
            latitude,
            longitude
          }, currentDate);
        }
      }
    });
  }

  ngOnInit(): void {
    // Check if we have saved value
    const savedValue = this.value();
    if (savedValue && savedValue.length > 0) {
      this.weatherData.set(savedValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fetchWeatherData(location: {
    city?: string;
    state?: string;
    zip?: string;
    latitude?: string;
    longitude?: string;
  }, date: Date): void {
    this.loading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.weatherService.getWeatherData(location, date).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.loading = false;
        if (data && data.length > 0) {
          this.weatherData.set(data);
          // Update the model value
          this.value.set(data);
          this.error = '';
        } else {
          this.error = 'Unable to fetch weather data. Please check the project location.';
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching weather:', err);
        this.loading = false;
        this.error = 'Failed to load weather data. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }

  private hasLocationData(
    latitude: string,
    longitude: string,
    zip: string,
    city: string,
    state: string
  ): boolean {
    return (!!latitude && !!longitude) || !!zip || (!!city && !!state);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
